// packages import
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, DialogActions, IconButton, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { FC, useContext } from 'react';
// others
import { CaseStatus, CreateNoteInput, ExternalUserRoleRole, useCreateNoteMutation } from '../../../../generated';
import { CANCEL, CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE, Note_CREATED_SUCCESS, SOMETHING_WENT_WRONG, STANDARD_DATE_FORMAT } from '../../../constants';
import { AuthContext, CaseContext } from '../../../context';
import { capitalizeCase, getPrimaryRole, titleize } from '../../../utils';
import { CaseActionType } from '../../cases/caseReducer';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { CustomController } from '../../common/CustomController';
import { CustomModal } from '../../common/CustomModal';
import { yupResolver } from '@hookform/resolvers/yup';
import { addCaseNoteValidationSchema } from '../../../validationSchema';
import { Alert } from '../../common/Alert';
import { DisplayItemProps } from '../../../interfaceTypes';
import { LeftChatBox } from '../../../theme/styleComponents';

const DisplayNoteItem: FC<DisplayItemProps> = ({ note, refetchAllNotes }) => {
  const { isNoteDeleteOpen, isNoteEditOpen, dispatch, currentCase, isCaseTaskOpen } = useContext(CaseContext)
  const { status, id: caseId, caseToExternalUserToExternalUserRole } = currentCase || {}
  const isClosed = status === CaseStatus.Completed
  const { currentUser, isParalegal } = useContext(AuthContext)
  const { id: currentUserId } = currentUser || {}
  const { createdAt, user, description, externalUser, isClientNote, isSmsNote, noteType } = note || {}
  const { firstName: externalUserFirstName, lastName: externalUserLastName } = externalUser || {}
  const externalUserFullName = `${externalUserFirstName} ${externalUserLastName}`
  const { roles: userRole, firstName: userFirstName, lastName: userLastName, id: userId } = user || {}
  const userFullName = `${userFirstName || '-'} ${userLastName || '-'}`
  const canDeleteOrEditNote = currentUserId !== userId
  const externalUserId = caseToExternalUserToExternalUserRole?.find((item) => item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles && item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role === ExternalUserRoleRole.Client)?.externalUserToExternalUserRole.externalUser?.id || ""

  const handleNoteDeleteModal = () => {
    dispatch({ type: CaseActionType.SET_IS_NOTE_DELETE_OPEN, isNoteDeleteOpen: !isNoteDeleteOpen })
    dispatch({ type: CaseActionType.SET_IS_CURRENT_NOTE, currentNote: note })
  }

  const handleNoteEditModal = () => {
    dispatch({ type: CaseActionType.SET_IS_NOTE_EDIT_OPEN, isNoteEditOpen: !isNoteEditOpen })
    dispatch({ type: CaseActionType.SET_IS_CURRENT_NOTE, currentNote: note })
  }

  const methods = useForm<CreateNoteInput>({
    mode: 'all',
    resolver: yupResolver(addCaseNoteValidationSchema),

    defaultValues: {
      description: '',
    }
  })

  const { handleSubmit, reset } = methods

  const [createNoteMutation, { loading: createNoteLoading }] = useCreateNoteMutation({
    onError(error) {
      if (error.message === CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE) Alert.error(CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE)
      else Alert.error(SOMETHING_WENT_WRONG);
    },

    onCompleted(data) {
      if (data) {
        const { createNote } = data;

        if (createNote) {
          const { response } = createNote;
          const { status } = response || {};

          if (status === 200) {
            Alert.success(Note_CREATED_SUCCESS);
            handleClose();
            refetchAllNotes && refetchAllNotes();
            reset()
          }
        }
      }
    },
  });


  const handleClose = () => {
    dispatch({ type: CaseActionType.SET_IS_CASE_TASK_OPEN, isCaseTaskOpen: false });
  };

  const onSubmit: SubmitHandler<CreateNoteInput> = async (data: CreateNoteInput) => {
    const { description, noteTypeId } = data || {}

    await createNoteMutation({
      variables: {
        createNoteInput: {
          description,
          noteTypeId,
          caseId: caseId!,
          externalUserId,
          userId: currentUser?.id || "null",
        },
      },
    });
  };

  return (
    <>
      {!isClientNote &&
        <Box pb={2}>
          <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap'>
            <Typography
              fontSize='16px'
              color='darkOne'
              marginBottom={2}
              paddingTop={1}
              sx={{ minWidth: 0, flex: '1 1 160px', overflowWrap: 'anywhere' }}
            >
              {description || ''}
            </Typography>

            <Box minWidth='81px' flexShrink={0} ml='auto'>
              <IconButton onClick={handleNoteEditModal} disabled={canDeleteOrEditNote} aria-label='edit note'>
                <EditIcon color={(canDeleteOrEditNote) ? 'disabled' : 'primary'} />
              </IconButton>

              <IconButton onClick={handleNoteDeleteModal} disabled={!isParalegal} aria-label='delete note'>
                <DeleteIcon color={(!isParalegal) ? 'disabled' : 'primary'} />
              </IconButton>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" columnGap={1}>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" color='darkOne' marginRight="14px" fontStyle='italic'>
                {capitalizeCase(userFullName)}
              </Typography>

              <Typography variant="subtitle1" color='lightGrayOne'>
                {titleize(getPrimaryRole(userRole) || noteType?.noteType || '')}
              </Typography>
            </Box>

            <Typography variant="subtitle1" color='lightGrayOne' pr={1.5}>
              {moment(parseInt(createdAt || '')).format(STANDARD_DATE_FORMAT)}
            </Typography>
          </Box>
        </Box>
      }

      {/* Left Chat Box */}
      {isClientNote &&
        <LeftChatBox marginRight={isSmsNote ? 'auto' : 'unset'} marginLeft={isSmsNote ? 'unset' : 'auto'}>
          <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap'>
            <Typography
              fontSize='16px'
              color='darkOne'
              marginBottom={2}
              paddingTop={1}
              sx={{ minWidth: 0, flex: '1 1 160px', overflowWrap: 'anywhere' }}
            >
              {description || ''}
            </Typography>

            {!isSmsNote &&
              <Box minWidth='81px' flexShrink={0} ml='auto'>
                <IconButton onClick={handleNoteEditModal} disabled={canDeleteOrEditNote || isClosed} aria-label='edit note'>
                  <EditIcon color={(canDeleteOrEditNote || isClosed) ? 'disabled' : 'primary'} />
                </IconButton>

                <IconButton onClick={handleNoteDeleteModal} disabled={!isParalegal || isClosed} aria-label='delete note'>
                  <DeleteIcon color={(!isParalegal || isClosed) ? 'disabled' : 'primary'} />
                </IconButton>
              </Box>
            }
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" columnGap={1}>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {isClientNote &&
                <Typography variant="subtitle1" color='darkOne' marginRight="14px" fontStyle='italic'>
                  {externalUser ? capitalizeCase(externalUserFullName) : capitalizeCase(userFullName)}
                </Typography>
              }

              {isSmsNote &&
                <>
                  <Typography variant="subtitle1" color='lightGrayOne'>
                    {userRole ? titleize(getPrimaryRole(userRole) || '') : 'Client'}
                  </Typography>

                  <Stack direction='row' alignItems='center' marginLeft={2}>
                    <Typography sx={{ color: 'primary', border: 1, borderColor: 'primary', borderRadius: 10, fontSize: 10, padding: '2px 5px' }}>SMS</Typography>
                  </Stack>
                </>
              }
            </Box>

            <Typography variant="subtitle1" color='lightGrayOne' pr={1.5}>
              {moment(parseInt(createdAt || '')).format(STANDARD_DATE_FORMAT)}
            </Typography>
          </Box>
        </LeftChatBox>
      }

      <CustomModal title="Create Note" isOpen={isCaseTaskOpen} handleClose={handleClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CustomController
              controllerLabel="Note"
              controllerName="description"
              isMultiLine
              rowsLength={5}
            />

            <DialogActions sx={{ padding: "30px 0 0" }}>
              <Button onClick={handleClose} variant='outlined' aria-label={CANCEL}>{CANCEL}</Button>
              <Button
                variant="contained"
                type="submit"
                disabled={createNoteLoading}
                aria-label='create note'
                endIcon={createNoteLoading && <CircularProgress size={20} color="inherit" />}
              >
                Create
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </CustomModal>
    </>
  )
}

export default DisplayNoteItem